import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils/classnames';

interface FlexBetweenProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** HTML element type (default: div) */
  as?: ElementType;
  /** Additional TailwindCSS classes */
  className?: string;
  /** Alignment on cross-axis (default: center) */
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  /** Gap between items */
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
}

/**
 * FlexBetween - Reusable layout primitive
 *
 * Replaces the repeated pattern: `flex items-center justify-between`
 * Used across 11+ files in the codebase.
 *
 * **Design Philosophy**:
 * - Single responsibility: space-between layout
 * - Composable: works with any children
 * - Accessible: forwards all HTML attributes
 * - Type-safe: explicit prop types
 *
 * **Usage**:
 * ```tsx
 * <FlexBetween>
 *   <h3>Title</h3>
 *   <Button>Action</Button>
 * </FlexBetween>
 *
 * // With custom gap and alignment
 * <FlexBetween gap={4} align="start">
 *   <div>Left</div>
 *   <div>Right</div>
 * </FlexBetween>
 *
 * // As a nav element
 * <FlexBetween as="nav">
 *   <Logo />
 *   <Menu />
 * </FlexBetween>
 * ```
 *
 * @see https://tailwindcss.com/docs/flex-direction
 */
export function FlexBetween({
  children,
  as: Component = 'div',
  className,
  align = 'center',
  gap,
  ...props
}: FlexBetweenProps) {
  return (
    <Component
      className={cn(
        'flex justify-between',
        {
          'items-start': align === 'start',
          'items-center': align === 'center',
          'items-end': align === 'end',
          'items-baseline': align === 'baseline',
          'items-stretch': align === 'stretch',
        },
        gap !== undefined && `gap-${gap}`,
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}
